import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileWord, FaFile } from 'react-icons/fa';
import { MdPictureAsPdf, MdClose } from 'react-icons/md';
import { Field } from 'formik';
import firebaseService from 'services/firebaseService.js';
import { PhotoUploadComponent } from 'components/Listing/RenderUploadPhotosForm';
import Switch from 'components/UI/Antd/Switch/Switch';

import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';

import Button from 'components/UI/Antd/Button/Button';
import Modal from 'components/UI/Antd/Modal/Modal';
import attachmentService from 'services/attachment.js';
import { message } from 'antd';
import isNil from 'lodash/isNil';
import loadingGif from 'assets/images/pickapier/loading.svg';
import {
  Wrapper,
  Label,
  Preview,
  PreviewWrapper,
  PreviewInner,
  Actions,
  LoaderWrapper,
  TypeIcon,
  ModalPreviewBody,
  ModalPreviewText,
  ModalActions,
  Action
} from './Document.style';

message.config({
  top: 100,
  duration: 7,
  maxCount: 1
});

const isImage = preview => preview.type.match(/image\/[^\s]+/g);

const Document = ({
  label,
  type,
  name,
  file,
  marinaName,
  vessel_id,
  onChange = () => {},
  onRemove = () => {},
  actionsAvailable = true,
  disabled = false
}) => {
  const { t } = useTranslation();
  const [previewFile, setPreviewFile] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [loaderVisible, setLoaderVisible] = useState(false);

  const uid = file ? file.id : null;

  useEffect(() => {
    async function getBoaterUrl() {
      const url = await firebaseService.getFileUrl(file.file_path);
      setPreviewFile({
        uid: file.id,
        name: file.name,
        url,
        type: file.file_type
      });
    }

    async function getVesselUrl() {
      const url = await attachmentService.getForVessel({
        attachment_id: file.id
      });
      setPreviewFile({
        uid: file.id,
        name: file.name,
        url,
        type: file.file_type
      });
    }

    if (!isNil(uid)) {
      if (isNil(vessel_id)) {
        getBoaterUrl();
      } else {
        getVesselUrl();
      }
    }
  }, [uid, vessel_id]);

  const onDelete = () => {
    setPreviewFile(null);
    const { id, name } = file;
    const data = { attachment_id: id };

    attachmentService.delete(data).then(res => {
      if (res && res.error_type) {
        message.error(`${name} attachment deleting failed`);
      } else {
        onRemove();
        setModalVisible(false);
        setLoaderVisible(false);
      }
    });
  };

  const onDownload = e => {
    e.preventDefault();
    const url = previewFile.url;

    const getFile = (filePath, cb) => {
      var xhr = new XMLHttpRequest();
      xhr.open('GET', filePath);
      xhr.responseType = 'blob';
      xhr.onload = function () {
        if (xhr.status && xhr.status === 200) {
          // cb(xhr.response)
          var url = window.URL.createObjectURL(xhr.response);
          var a = document.createElement('a');
          a.href = url;
          a.download = previewFile.name;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      };
      xhr.send();
    };

    getFile(url);
  };

  const getTypeIcon = type => {
    switch (type) {
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return <FaFileWord color='#005698' size={50} />;
      case 'application/msword':
        return <FaFileWord color='#005698' size={50} />;
      case 'application/pdf':
        return <MdPictureAsPdf color='#ea4335' size={50} />;
      default:
        return <FaFile color='green' size={50} />;
    }
  };

  return (
    <Wrapper className='document-wrapper'>
      <Label>{label}</Label>
      {file || loaderVisible ? (
        <Preview className={disabled ? 'disabled' : ''}>
          {previewFile ? (
            <Fragment>
              <PreviewWrapper className={actionsAvailable ? '' : 'no-actions'}>
                <PreviewInner onClick={() => setModalVisible(true)}>
                  {isImage(previewFile) ? (
                    <img alt='Preview pic' src={previewFile.url} />
                  ) : (
                    <TypeIcon>{getTypeIcon(previewFile.type)}</TypeIcon>
                  )}
                </PreviewInner>
                {!marinaName && (
                  <Button
                    className='delete_button'
                    htmlType='button'
                    onClick={onDelete}
                    type='link'
                  >
                    <DeleteOutlined />
                  </Button>
                )}
              </PreviewWrapper>
            </Fragment>
          ) : (
            <LoaderWrapper>
              <img alt='Preview pic' src={loadingGif} />
            </LoaderWrapper>
          )}
          {!actionsAvailable && (
            <Field
              hidden={true}
              disabled={disabled}
              component={PhotoUploadComponent}
              vessel_id={vessel_id}
              attachmentType={type}
              name={name}
              type='file'
              onChange={value => {
                setLoaderVisible(true);
                onChange(value);
              }}
              accept='image/*,.pdf,.doc,.docx'
            />
          )}
        </Preview>
      ) : (
        <Field
          disabled={disabled}
          component={PhotoUploadComponent}
          attachmentType={type}
          vessel_id={vessel_id}
          name={name}
          type='file'
          onChange={value => {
            setLoaderVisible(true);
            onChange(value);
          }}
          accept='image/*,.pdf,.doc,.docx'
        />
      )}
      {previewFile && (
        <Modal
          wrapClassName='documents-preview-modal'
          width={isImage(previewFile) ? '90%' : 570}
          centered={true}
          visible={modalVisible}
          title={label}
          footer={
            actionsAvailable && (
              <ModalActions>
                <Action>
                  <Button
                    className='action_button'
                    htmlType='button'
                    onClick={onDownload}
                    type='link'
                  >
                    <DownloadOutlined />
                  </Button>
                  <span>{t('DOWNLOAD')}</span>
                </Action>
                {!marinaName && (
                  <Action>
                    <Button
                      className='action_button'
                      htmlType='button'
                      onClick={onDelete}
                      type='link'
                    >
                      <DeleteOutlined />
                    </Button>
                    <span>{t('DELETE')}</span>
                  </Action>
                )}
              </ModalActions>
            )
          }
          onCancel={() => setModalVisible(false)}
          closeIcon={<MdClose size={24} />}
        >
          {isImage(previewFile) ? (
            <img
              alt='document'
              style={{ width: '100%' }}
              src={previewFile.url}
            />
          ) : (
            <ModalPreviewBody>
              <TypeIcon>{getTypeIcon(previewFile.type)}</TypeIcon>
              <ModalPreviewText>
                {t('PREVIEW_IS_NOT_AVAILABLE')}
                {previewFile.type === 'application/pdf' ? (
                  <Fragment>
                    ,&nbsp;
                    <a href={previewFile.url} target='_blank' download>
                      {t('OPEN_TO_VIEW')}
                    </a>
                  </Fragment>
                ) : (
                  <Fragment>
                    ,&nbsp;
                    <a href='#' onClick={onDownload}>
                      {t('DOWNLOAD_TO_VIEW')}
                    </a>
                  </Fragment>
                )}
              </ModalPreviewText>
            </ModalPreviewBody>
          )}
        </Modal>
      )}
    </Wrapper>
  );
};

export default Document;
